import { createContext, useContext, useMemo } from "react";
import { Component, ComponentType } from "@/types";
import type {
  InteractionCapabilities,
  InteractionContextValue,
  InteractionHandlers,
  InteractionMode,
  UserPermissions,
} from "./InteractionContext";

const defaultContextValue: InteractionContextValue = {
  mode: "viewing",
  capabilities: {
    canSelect: false,
    canResize: false,
    canDrag: false,
    canInteract: false,
    canNavigate: false,
    canEditProperties: false,
    canDelete: false,
  },
  handlers: {},
  permissions: {
    canEdit: false,
    canView: true,
    canInteract: false,
    isOwner: false,
  },
  isEditMode: () => false,
  isViewMode: () => true,
  isFunctionalMode: () => false,
  canInteractWith: () => false,
};

export const InteractionContext = createContext<InteractionContextValue>(defaultContextValue);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isManualOnlyAction = (action: unknown): boolean => {
  if (!isRecord(action)) return false;
  return action.manualOnly === true;
};

export const shouldBlockSensorControl = (component: Component): boolean =>
  isManualOnlyAction(component.properties?.action);

// Custom hook to use the interaction context
export const useInteractionContext = () => {
  const context = useContext(InteractionContext);
  if (!context) {
    console.warn("useInteractionContext must be used within InteractionContextProvider");
    return defaultContextValue;
  }
  return context;
};

// Determine capabilities based on mode and permissions
export const determineCapabilities = (
  mode: InteractionMode,
  permissions?: UserPermissions
): InteractionCapabilities => {
  const perms = permissions || {
    canEdit: false,
    canView: true,
    canInteract: false,
    isOwner: false,
  };

  switch (mode) {
    case "editing":
      return {
        canSelect: perms.canEdit,
        canResize: perms.canEdit,
        canDrag: perms.canEdit,
        canInteract: false,
        canNavigate: false,
        canEditProperties: perms.canEdit,
        canDelete: perms.canEdit,
      };

    case "viewing":
      return {
        canSelect: false,
        canResize: false,
        canDrag: false,
        canInteract: false,
        canNavigate: false,
        canEditProperties: false,
        canDelete: false,
      };

    case "functional":
      return {
        canSelect: false,
        canResize: false,
        canDrag: false,
        canInteract: perms.canInteract,
        canNavigate: perms.canInteract,
        canEditProperties: false,
        canDelete: false,
      };

    case "pin-placement":
      return {
        canSelect: false,
        canResize: false,
        canDrag: false,
        canInteract: false,
        canNavigate: false,
        canEditProperties: false,
        canDelete: false,
        canPlacePin: perms.canEdit,
      };

    default:
      return defaultContextValue.capabilities;
  }
};

// Component-specific interaction hook
export const useComponentInteraction = (component: Component) => {
  const context = useInteractionContext();

  return useMemo(() => {
    const canInteract = context.canInteractWith(component);
    const sensorControlBlocked = shouldBlockSensorControl(component);

    const applicableHandlers: Partial<InteractionHandlers> = {};

    const attachSensorControlHandler = () => {
      if (!sensorControlBlocked) {
        applicableHandlers.onSensorControl = context.handlers.onSensorControl;
      }
    };

    if (context.isEditMode() && canInteract) {
      applicableHandlers.onSelect = context.handlers.onSelect;
      applicableHandlers.onResize = context.handlers.onResize;
      applicableHandlers.onDelete = context.handlers.onDelete;
      applicableHandlers.onEditProperties = context.handlers.onEditProperties;
    }

    if (context.isFunctionalMode() && canInteract) {
      const navigableTypes = [
        ComponentType.BUTTON,
        ComponentType.ICON,
        ComponentType.BAR_CHART,
        ComponentType.LINE_CHART,
        ComponentType.PIE_CHART,
        ComponentType.AREA_CHART,
        ComponentType.SCATTER_CHART,
        ComponentType.HEATMAP_CHART,
        ComponentType.TREEMAP_CHART,
        ComponentType.MAP_CHART,
      ];

      const hasNavigationProperties = component.properties?.action === "navigate"
        && typeof component.properties?.target === "string";
      if (navigableTypes.includes(component.type) || hasNavigationProperties) {
        applicableHandlers.onNavigate = context.handlers.onNavigate;
      }

      if (component.type === ComponentType.BUTTON) {
        applicableHandlers.onApiCall = context.handlers.onApiCall;
        attachSensorControlHandler();
        applicableHandlers.onToggleVisibility = context.handlers.onToggleVisibility;
        applicableHandlers.onSwapContent = context.handlers.onSwapContent;
        applicableHandlers.onAddTableRow = context.handlers.onAddTableRow;
        applicableHandlers.onAddChecklistItem = context.handlers.onAddChecklistItem;
        applicableHandlers.onFormSubmit = context.handlers.onFormSubmit;
        applicableHandlers.onNavigateProject = context.handlers.onNavigateProject;
      }

      if (component.type === ComponentType.INPUT) {
        applicableHandlers.onFormSubmit = context.handlers.onFormSubmit;
      }

      if (component.type === ComponentType.TABLE || component.type === ComponentType.ADVANCED_TABLE) {
        applicableHandlers.onEditTableRow = context.handlers.onEditTableRow;
        applicableHandlers.onDeleteTableRow = context.handlers.onDeleteTableRow;
      }

      if (component.type === ComponentType.FILE_STORAGE) {
        applicableHandlers.onOpenFileManager = context.handlers.onOpenFileManager;
        console.log("[FileStorage:Interaction] Handler registered id=", component.id);
      }

      if (component.type === ComponentType.CHECKLIST) {
        applicableHandlers.onAddChecklistItem = context.handlers.onAddChecklistItem;
      }

      if (component.type === ComponentType.GANTT_CHART) {
        applicableHandlers.onAddGanttTask = context.handlers.onAddGanttTask;
        applicableHandlers.onEditGanttTask = context.handlers.onEditGanttTask;
      }

      if (component.type === ComponentType.KANBAN_BOARD) {
        applicableHandlers.onAddKanbanItem = context.handlers.onAddKanbanItem;
        applicableHandlers.onEditKanbanItem = context.handlers.onEditKanbanItem;
      }

      if (component.type === ComponentType.RADIAL_DIAL) {
        attachSensorControlHandler();
      }

      if (component.type === ComponentType.TOGGLE_SWITCH) {
        attachSensorControlHandler();
      }

      if (component.type === ComponentType.PUSH_BUTTON) {
        attachSensorControlHandler();
      }

      if (component.type === ComponentType.ICON_BUTTON) {
        attachSensorControlHandler();
      }

      if (component.type === ComponentType.STEPPER) {
        attachSensorControlHandler();
      }

      if (component.type === ComponentType.SLIDER) {
        attachSensorControlHandler();
      }

      if (component.type === ComponentType.SEGMENTED_CONTROL) {
        attachSensorControlHandler();
      }

      if (component.type === ComponentType.COLOR_PICKER) {
        attachSensorControlHandler();
      }

      if (component.type === ComponentType.THERMOSTAT) {
        attachSensorControlHandler();
      }

      if (component.properties?.collaborative) {
        applicableHandlers.onCollaboration = context.handlers.onCollaboration;
      }
    }

    return {
      canInteract,
      handlers: applicableHandlers,
      mode: context.mode,
      capabilities: context.capabilities,
    };
  }, [component, context]);
};
